import { useState, createContext, useContext, useEffect } from 'react'
import { NavLink, useParams, useNavigate } from 'react-router-dom'
import {
  AppWindow,
  Users,
  Settings,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Building2,
  LayoutDashboard,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui'

// Sidebar state context
interface SidebarContextValue {
  isCollapsed: boolean
  setIsCollapsed: (value: boolean) => void
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

export function useSidebarState() {
  const context = useContext(SidebarContext)
  if (!context) {
    // Return default values if not in provider
    return { isCollapsed: false, setIsCollapsed: () => {} }
  }
  return context
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('console-sidebar-collapsed') === 'true'
  })

  useEffect(() => {
    localStorage.setItem('console-sidebar-collapsed', String(isCollapsed))
  }, [isCollapsed])

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '' },
  { icon: AppWindow, label: 'Apps', href: 'apps' },
  { icon: Users, label: 'Team', href: 'team' },
  { icon: Settings, label: 'Settings', href: 'settings' },
  { icon: CreditCard, label: 'Billing', href: 'billing' },
]

export function OrgSidebar() {
  const { orgSlug } = useParams()
  const { isCollapsed, setIsCollapsed } = useSidebarState()
  const navigate = useNavigate()

  const baseUrl = orgSlug ? `/orgs/${orgSlug}` : ''

  const handleOrgClick = () => {
    if (orgSlug) {
      navigate(`/orgs/${orgSlug}`)
    }
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-14 z-30 hidden h-[calc(100vh-3.5rem)] border-r border-[var(--border-default)] bg-[var(--bg-base)] transition-[width] duration-200 lg:block',
        isCollapsed ? 'w-16' : 'w-60'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Org selector - clicks navigate to org dashboard */}
        <div className="border-b border-[var(--border-subtle)] p-3">
          <button
            onClick={handleOrgClick}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg bg-[var(--bg-elevated)] px-3 py-2 text-left transition-colors hover:bg-[var(--bg-elevated-2)]',
              isCollapsed && 'justify-center px-2'
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--accent)] text-white">
              <Building2 className="h-4 w-4" />
            </div>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                  {orgSlug || 'Select Org'}
                </p>
                <p className="truncate text-xs text-[var(--text-muted)]">
                  Organization
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const href = item.href ? `${baseUrl}/${item.href}` : baseUrl
              const isExact = item.href === ''
              return (
                <li key={item.label}>
                  <NavLink
                    to={href}
                    end={isExact}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-[var(--accent)]/10 text-[var(--accent)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
                        isCollapsed && 'justify-center px-2'
                      )
                    }
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Collapse toggle */}
        <div className="border-t border-[var(--border-subtle)] p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn('w-full', isCollapsed ? 'justify-center px-2' : 'justify-start')}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="mr-2 h-4 w-4" />
                Collapse
              </>
            )}
          </Button>
        </div>
      </div>
    </aside>
  )
}
